import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure.js";
import DocumentRef from "./DocumentRef.js";
import Address from "./Address.js";
import { ShipTo } from "./Quote.js";
import { COLORS } from "../constants.js";
import { deepFreeze, timestampToDate } from "../util/misc.js";
import { cloneDeep, compact, every, get, isEmpty, sortBy, sumBy } from "lodash";
import { diff } from "deep-object-diff";

export const SERVICE_WORK_ORDER_FIELDS = {
  // Project/Sale reference
  projectID: "",
  saleID: "",

  // Service Address (can be set independently of project)
  serviceAddress: {
    street: "",
    city: "",
    state: "",
    zip: "",
  },

  // Service type
  serviceType: "", // warranty, chargeable, punchout, inspection, referred

  // Issues included in this work order
  issueIDs: [],

  // Scheduling
  scheduledDate: null,
  scheduledStartTime: null,
  scheduledEndTime: null,
  eventID: "", // Link to CalendarEvent

  // Technicians
  assignedTechs: [],

  // Vehicles assigned to the visit (mirrored onto the CalendarEvent)
  vehicles: [],

  // Service contact (person to meet on site)
  serviceContactID: "",
  serviceContactName: "",
  serviceContactPhone: "",

  // Time tracking
  startedDate: null, // Timestamp when tech clicks "Start Service"
  estimatedHours: 0, // Sum of issue estimates
  estimatedManHours: 0, // estimatedHours * techs
  actualHours: 0,
  actualManHours: 0,

  // Location
  location: null, // ShipTo address

  // Status
  status: "pending", // pending, scheduled, in_progress, completed, cancelled

  // Completion
  completedDate: null,
  completedBy: null,
  signature: null,
  signerName: "",
  noCustomerOnSite: false, // Tech-side flag: nobody was present to sign
  completedIssueIDs: [], // Issues resolved by this visit
  followUpNeeded: false, // True when any issue was deferred to a later visit
  completedWithoutVisit: false, // Closed out by the office: no visit, nobody notified

  // Notes (consolidated - internal notes only)
  internalNotes: "", // Internal notes (client doesn't see)
  completionNotes: "", // Notes after completion

  // Legacy notes fields (for backwards compatibility)
  notes: "",
  techNotes: "",

  // New issues found during service
  newIssueIDs: [],

  // Attachments
  attachments: [],

  // Parts loading
  partsToLoad: [], // List of part IDs expected for this work order
  partsAction: {}, // { partID: "deliver" | "deliver_install" } - action for each part
  loadedParts: {}, // { partID: { loaded: true, loadedBy, loadedAt, location } }
  deliveredParts: {}, // { partID: { delivered: true, deliveredBy, deliveredAt } }
  loadingStarted: null,
  loadingCompleted: null,

  // Billing
  isBillable: false,
  billableTo: "",
  invoiceID: "",
  isCashSale: false, // Flag for cash collection reminder

  // Flags
  isInspection: false, // Visit is to verify/confirm scope, not complete the repair
  isPunchout: false,
  punchoutItemCount: 0, // For calculating punchout duration (5 min/item)
  punchoutCompleted: false, // Whether punchout has been done for this project
  partsReady: false, // All required parts have been received
  locked: false,

  // Completion signoff
  noSignatureReason: "", // Reason if customer not present to sign
  completionPhotos: [], // Required photos of completed work

  // Tech checklist tracking
  techCompletedIssues: [], // Issues checked off by tech during service
  techDeferredIssues: [], // Issues deferred by tech (needs parts/quote/another visit)

  // Tasks typed on the work order for the tech to tick off on site. The list
  // is the office's, in order; the ticks are the tech's, keyed by task id and
  // written one path at a time (like deliveredParts) so ticking never rewrites
  // the list.
  tasks: [], // [{ id, text }]
  checkedTasks: {}, // { taskID: { checked: true, checkedAt, checkedBy } }

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(SERVICE_WORK_ORDER_FIELDS);

// Default duration (hours) for an inspection visit. The scheduler can edit
// this on the work order / calendar; it is not derived from the issues'
// remedy estimates because an inspection only verifies scope.
export const INSPECTION_DEFAULT_HOURS = 0.5;

export const WORK_ORDER_STATUS = {
  pending: {
    order: 1,
    label: "Pending",
    icon: "pending",
    color: "grey",
  },
  scheduled: {
    order: 2,
    label: "Scheduled",
    icon: "date_done",
    color: "info",
  },
  in_progress: {
    order: 3,
    label: "In progress",
    icon: "pending",
    color: "warning",
  },
  completed: {
    order: 4,
    label: "Completed",
    icon: "done",
    color: "positive",
  },
  cancelled: {
    order: 5,
    label: "Canceled",
    icon: "cancel",
    color: "negative",
  },
};
deepFreeze(WORK_ORDER_STATUS);

export default class ServiceWorkOrder extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, SERVICE_WORK_ORDER_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.scheduledDate = timestampToDate(data.scheduledDate);
    data.startedDate = timestampToDate(data.startedDate);
    data.completedDate = timestampToDate(data.completedDate);
    data.loadingStarted = timestampToDate(data.loadingStarted);
    data.loadingCompleted = timestampToDate(data.loadingCompleted);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
      complete: this.canBeCompletedBy,
    });

    // Documents
    if (this.data.attachments) {
      this.data.attachments = this.attachments.map((d) => d.data);
    }
  }

  // Permissions

  canBeEditedBy(user) {
    return (
      (!this.id || user.isAdmin || !this.locked) &&
      user.isEmployee &&
      (user.id === this.data.createdBy ||
        this.data.assignedTechs.includes(user.id) ||
        user.isAdmin)
    );
  }

  canBeDeletedBy(user) {
    return (
      !this.locked &&
      this.data.status === "pending" &&
      user.isEmployee &&
      (user.id === this.data.createdBy || user.isAdmin)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  canBeCompletedBy(user) {
    return (
      !this.locked &&
      this.data.status !== "completed" &&
      this.data.status !== "cancelled" &&
      user.isEmployee &&
      (this.data.assignedTechs.includes(user.id) || user.isAdmin)
    );
  }

  // Tasks

  isTaskChecked(taskID) {
    return Boolean(this.data.checkedTasks?.[taskID]?.checked);
  }

  get uncheckedTasks() {
    return this.data.tasks.filter((task) => !this.isTaskChecked(task.id));
  }

  // Database

  get path() {
    return ServiceWorkOrder.getPath(this.id);
  }

  static getPath(id) {
    let path = `serviceWorkOrders`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(workOrder) {
    return workOrder.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new ServiceWorkOrder(snapshot.id, data);
  }

  // Validation

  get rules() {
    const required = (a) => Boolean(a && String(a).trim().length);
    return {
      projectID: [required],
      saleID: [required],
      serviceType: [required],
      assignedTechs: [(a) => a && a.length > 0],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every(
        (rule) =>
          rule(get(this.data, key)) ||
          console.info(`Invalid ${key}`, get(this.data, key))
      )
    );
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(
        diff(
          ServiceWorkOrder.sanitize({
            ...this.data,
            projectID: "",
            saleID: "",
          }),
          SERVICE_WORK_ORDER_FIELDS
        )
      )
    );
  }

  isEqual(workOrder) {
    return (
      this.id === workOrder.id &&
      isEmpty(
        diff(
          ServiceWorkOrder.sanitize(cloneDeep(this.data)),
          ServiceWorkOrder.sanitize(cloneDeep(workOrder.data))
        )
      )
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (data.attachments) {
      data.attachments = data.attachments.filter(
        (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
      );
    }
    return data;
  }

  static sanitize(data, removeUnsaved) {
    ServiceWorkOrder.cleanDocuments(data, removeUnsaved);
    return data;
  }

  // Calculated fields

  calculateEstimates() {
    const issues = this.issues;
    if (this.data.isInspection) {
      // Inspection duration is set manually (fixed default, editable) and is
      // NOT derived from the issues' remedy estimates — an inspection only
      // verifies scope, so it typically takes far less time than the repair.
      if (!this.data.estimatedHours) {
        this.data.estimatedHours = INSPECTION_DEFAULT_HOURS;
      }
    } else if (this.data.isPunchout) {
      // Punchout: 5 minutes per item
      this.data.estimatedHours = (this.data.punchoutItemCount * 5) / 60;
    } else {
      this.data.estimatedHours = sumBy(issues, "data.estimatedHours") || 0;
    }
    this.data.estimatedManHours =
      this.data.estimatedHours * this.data.assignedTechs.length;
    return this;
  }

  get isInspection() {
    return Boolean(this.data.isInspection);
  }

  // Formatting

  get project() {
    return this.data.projectID
      ? store.state.main.projects[this.data.projectID]
      : null;
  }

  get sale() {
    return this.data.saleID
      ? store.state.main.sales[this.data.projectID + this.data.saleID]
      : null;
  }

  // The PO the job is tracked by (project ID + sale letter), as on
  // ServiceIssue.
  get po() {
    return (this.data.projectID || "") + (this.data.saleID || "");
  }

  get issues() {
    return compact(
      this.data.issueIDs.map((id) => store.state.main.serviceIssues[id])
    );
  }

  get newIssues() {
    return compact(
      this.data.newIssueIDs.map((id) => store.state.main.serviceIssues[id])
    );
  }

  get event() {
    return this.data.eventID
      ? store.state.main.events[this.data.eventID]
      : null;
  }

  get assignedTechs() {
    return compact(
      this.data.assignedTechs.map((id) => store.state.main.users[id])
    );
  }

  get location() {
    if (!this.data.location) {
      return this.sale ? this.sale.shipTo : null;
    }
    if ("address" in this.data.location) {
      return new ShipTo(this.data.location);
    }
    return new Address(this.data.location);
  }

  get statusType() {
    const type = WORK_ORDER_STATUS[this.data.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get attachments() {
    return this.data.attachments.map((d) => new DocumentRef(d));
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get completedBy() {
    return this.data.completedBy
      ? store.state.main.users[this.data.completedBy]
      : null;
  }

  // Issue summary

  get openIssueCount() {
    return this.issues.filter((i) => !i.data.isResolved).length;
  }

  get resolvedIssueCount() {
    return this.issues.filter((i) => i.data.isResolved).length;
  }

  get partsRequiredIssues() {
    return this.issues.filter(
      (i) => i.data.partsRequired && !i.data.partsReceived
    );
  }

  get readyIssues() {
    return this.issues.filter(
      (i) => !i.data.partsRequired || i.data.partsReceived
    );
  }

  get allPartsReady() {
    return this.partsRequiredIssues.length === 0;
  }

  get hasRequiredPhotos() {
    // Minimum 2 photos required for completion
    return this.data.completionPhotos && this.data.completionPhotos.length >= 2;
  }

  get canComplete() {
    // Can complete if: has signature OR has no-signature reason, AND has required photos
    const hasSignoff =
      this.data.signature || this.data.noSignatureReason.trim().length > 0;
    return hasSignoff && this.hasRequiredPhotos;
  }

  toString() {
    const project = this.project;
    return project ? `Service - ${project.toString()}` : `Service Work Order`;
  }
}
